"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useWaitForVideoEvaluator = void 0;
const react_1 = require("react");
/**
 * Evaluator for pages where video content is important to not be interrupted (e.g. memories)
 *
 * This evaluator will first wait for `visibilityCondition.value.initialWaitTimeMs` milliseconds for the video to be played.
 * If the video has not started even after that time, we can show the CTA Footer
 *
 * If the video has been started, then we either wait for the video to end, or for the user to manually pause the video.
 * The condition to show the CTA footer gets fulfilled in both cases
 */
function useWaitForVideoEvaluator(visibilityCondition) {
    const [visibilityConditionFulfilled, setVisibilityConditionFulfilled] = (0, react_1.useState)(false);
    // to tell whether the video ever started playing
    const startedPlaying = (0, react_1.useRef)(false);
    useInitialWaitTime(visibilityCondition, setVisibilityConditionFulfilled, startedPlaying);
    const handleVideoPlay = (0, react_1.useCallback)(() => {
        startedPlaying.current = true;
    }, []);
    const handleVideoInterrupted = (0, react_1.useCallback)(() => {
        const videoElement = visibilityCondition.videoElement;
        if (startedPlaying.current && (videoElement?.readyState ?? 0) > 2) {
            // Safe condition for browsers that fire "ended/paused" event even before the video ever started playing
            // In some browsers, the "ended/pause" event listener is also fired when the video has no more data to load (buffering).
            // We want to avoid that state, so checking the readyState to make sure we are not taking action on those events
            setVisibilityConditionFulfilled(true);
        }
    }, [visibilityCondition]);
    // Responsible for setting event listeners to the video
    (0, react_1.useEffect)(() => {
        const videoElement = visibilityCondition.videoElement;
        videoElement?.addEventListener("play", handleVideoPlay);
        videoElement?.addEventListener("pause", handleVideoInterrupted);
        videoElement?.addEventListener("ended", handleVideoInterrupted);
        return () => {
            videoElement?.removeEventListener("play", handleVideoPlay);
            videoElement?.removeEventListener("pause", handleVideoInterrupted);
            videoElement?.removeEventListener("ended", handleVideoInterrupted);
        };
    }, [visibilityCondition, handleVideoPlay, handleVideoInterrupted]);
    return visibilityConditionFulfilled;
}
exports.useWaitForVideoEvaluator = useWaitForVideoEvaluator;
/**
 * Adds a timeout for the initial wait time to show the CTA footer if the video has not been started in that timeout
 */
function useInitialWaitTime(visibilityCondition, setVisibilityConditionFulfilled, startedPlaying) {
    const visibilityConditionRef = (0, react_1.useRef)(visibilityCondition);
    visibilityConditionRef.current = visibilityCondition;
    (0, react_1.useEffect)(() => {
        const { initialWaitTimeMs } = visibilityConditionRef.current;
        let isMounted = true;
        const timeoutId = setTimeout(() => {
            if (!startedPlaying.current && isMounted) {
                // it has been initialWaitTimeMs time since the component was mounted, and the video still hasnt been played
                // this means we can show the CTA Footer now
                setVisibilityConditionFulfilled(true);
            }
        }, initialWaitTimeMs);
        return () => {
            isMounted = false;
            clearTimeout(timeoutId);
        };
        //   We dont want this effect to run multiple times.
        //   Just once after the mount. Make sure the dependency array has non-changing values at all times:
    }, [startedPlaying, setVisibilityConditionFulfilled]);
}
//# sourceMappingURL=useWaitForVideoEvaluator.js.map